import { AP_SOUTH_1 } from '@tryhackme/interfaces';
import { useCallback } from 'react';
import { UserOccupation } from 'src/common/constants/user';
import { useGetUserQuery } from 'src/common/slices';
import { B2BUserHelper, isSocSimInternalQA } from '../helpers';

const featureMap = {
  'soc-sim': 'socSim',
  'threat-hunting': 'threatHunting',
};

export const useScenarioPermissions = (type: keyof typeof featureMap = 'soc-sim') => {
  const { data: userQueryData, isLoading: isUserLoading } = useGetUserQuery();
  const user = userQueryData?.data?.user;
  const isApSouthOneUser = user?.local?.vmRegion === AP_SOUTH_1;

  const company = user?.subscriptions.find((sub) => sub.type === 'company' && sub.status === 'active');
  const companyId = company?.companyId;
  const feature = featureMap[type];

  const { isCompanyAdmin, isEduAdminOnly } = B2BUserHelper(user);

  const hasAccessToB2B =
    !isUserLoading &&
    // The company must have the feature enabled
    !!company?.features?.[feature as keyof typeof company.features] &&
    // AND one of these conditions must be met:
    (Boolean(companyId) ||
      // B2B admins need to be premium (edu admins cannot access B2B scenarios)
      (isCompanyAdmin && user?.isPremium && !isEduAdminOnly) ||
      // Edu admins can access B2B scenarios if the company has the feature enabled
      isEduAdminOnly);

  const isEdu = company?.companyType === 'edu';
  const hasAccessToB2C =
    !isUserLoading && (user?.isPremium || Boolean(companyId) || isEdu || isEduAdminOnly || isCompanyAdmin);
  // @TODO: Refactor - remove content-dev - https://tryhackme.atlassian.net/browse/PLAT-540
  const hasAccessToQAScenarios = isSocSimInternalQA(user);
  const isLoggedIn = Boolean(user);

  const shouldPromoteToSelfServe =
    user?.isPremium &&
    [UserOccupation.CYBER_PRACTITIONER, UserOccupation.MANAGER].includes(user?.business?.occupation as UserOccupation);

  const canAccessScenario = useCallback(
    (flags: string[]) => {
      // Not logged in users can't access any scenarios
      if (!user) return false;

      // Handle qa scenarios - only content dev users
      if (flags.includes('qa_testing')) return hasAccessToQAScenarios;

      // Handle coming soon - no access for anyone
      if (flags.includes('coming_soon')) return false;

      // Handle free scenarios - no restrictions
      if (flags.includes('free')) return true;

      // Handle B2C scenarios - Premium or B2B users. Edu admins/users can access B2C scenarios
      if (flags.includes('b2c')) {
        return hasAccessToB2C;
      }

      // Handle B2B scenarios with socSim feature. Edu users cannot access B2B scenarios
      if (flags.includes('b2b')) {
        return hasAccessToB2B;
      }

      return false;
    },
    [user, hasAccessToB2B, hasAccessToB2C, hasAccessToQAScenarios],
  );

  return {
    canAccessScenario,
    hasAccessToB2B,
    hasAccessToB2C,
    hasAccessToQAScenarios,
    isLoggedIn,
    isEdu,
    isEduAdminOnly,
    isApSouthOneUser,
    shouldPromoteToSelfServe,
  };
};
